import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";
import { ArrowLeft, Plus, DollarSign, Clock, Package, Pencil, Trash2, Tag } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";

export default function ServiceCatalog() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [services, setServices] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    name: "", description: "", pricing_type: "hourly", price: "",
    estimated_hours: "", product_id: "", is_active: true,
  });

  const fetchData = useCallback(async () => {
    try {
      const [sRes, pRes] = await Promise.all([
        apiClient.get("/services?active_only=false"),
        apiClient.get("/products"),
      ]);
      setServices(sRes.data);
      setProducts(pRes.data);
    } catch { toast.error("Failed to load services"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const resetForm = () => {
    setForm({ name: "", description: "", pricing_type: "hourly", price: "", estimated_hours: "", product_id: "", is_active: true });
    setEditingId(null);
  };

  const openEdit = (s) => {
    setForm({
      name: s.name, description: s.description, pricing_type: s.pricing_type,
      price: String(s.price), estimated_hours: s.estimated_hours ? String(s.estimated_hours) : "",
      product_id: s.product_id || "", is_active: s.is_active,
    });
    setEditingId(s.id);
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form, price: parseFloat(form.price),
      estimated_hours: form.estimated_hours ? parseFloat(form.estimated_hours) : null,
      product_id: form.product_id === "none" ? null : form.product_id || null,
    };
    try {
      if (editingId) {
        await apiClient.patch(`/services/${editingId}`, payload);
        toast.success("Service updated");
      } else {
        await apiClient.post("/services", payload);
        toast.success("Service created");
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save service");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this service?")) return;
    try {
      await apiClient.delete(`/services/${id}`);
      toast.success("Service deleted");
      fetchData();
    } catch { toast.error("Failed to delete"); }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="service-catalog-page">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/admin")}><ArrowLeft className="h-5 w-5" /></Button>
          <div className="flex-1">
            <h1 className="font-heading text-2xl font-bold text-slate-900">{t("services.serviceCatalog")}</h1>
            <p className="text-slate-500 mt-1">{t("services.manageServices")}</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-sag-navy hover:bg-sag-navy-800" data-testid="create-service-button">
                <Plus className="h-4 w-4 mr-2" /> {t("services.addService")}
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">{editingId ? "Edit" : "Create"} Service</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Service Name *</Label>
                  <Input placeholder="e.g., Website Troubleshooting" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="service-name-input" />
                </div>
                <div className="space-y-2">
                  <Label>Description *</Label>
                  <Textarea placeholder="What does this service include..." value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} required />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Pricing Type *</Label>
                    <Select value={form.pricing_type} onValueChange={(v) => setForm({ ...form, pricing_type: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="hourly">Hourly Rate</SelectItem>
                        <SelectItem value="fixed">Fixed Price</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Price (USD) *</Label>
                    <Input type="number" step="0.01" min="1" placeholder={form.pricing_type === "hourly" ? "200.00/hr" : "500.00"} value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} required data-testid="service-price-input" />
                  </div>
                </div>
                {form.pricing_type === "hourly" && (
                  <div className="space-y-2">
                    <Label>Estimated Hours (optional)</Label>
                    <Input type="number" step="0.5" min="0.5" placeholder="2" value={form.estimated_hours} onChange={(e) => setForm({ ...form, estimated_hours: e.target.value })} />
                  </div>
                )}
                <div className="space-y-2">
                  <Label>Product (optional)</Label>
                  <Select value={form.product_id || "none"} onValueChange={(v) => setForm({ ...form, product_id: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">All Products</SelectItem>
                      {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>Cancel</Button>
                  <Button type="submit" className="bg-sag-navy hover:bg-sag-navy-800" data-testid="submit-service">{editingId ? "Save" : "Create"}</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Card className="sag-card">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-slate-500">Loading...</div>
            ) : services.length === 0 ? (
              <div className="p-8 text-center">
                <Package className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">{t("services.noServicesAdmin")}</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Service</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Price</TableHead>
                    <TableHead>Product</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {services.map((s) => (
                    <TableRow key={s.id}>
                      <TableCell>
                        <div className="font-medium text-slate-900">{s.name}</div>
                        <div className="text-xs text-slate-500 max-w-[250px] truncate">{s.description}</div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className={s.pricing_type === "hourly" ? "bg-blue-100 text-blue-700" : "bg-purple-100 text-purple-700"}>
                          {s.pricing_type === "hourly" ? <><Clock className="h-3 w-3 mr-1" />Hourly</> : <><Tag className="h-3 w-3 mr-1" />Fixed</>}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-semibold text-slate-900">
                        ${s.price.toFixed(2)}{s.pricing_type === "hourly" ? "/hr" : ""}
                      </TableCell>
                      <TableCell className="text-slate-600">{s.product_name || "All"}</TableCell>
                      <TableCell>
                        <Badge variant="secondary" className={s.is_active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}>
                          {s.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openEdit(s)}><Pencil className="h-4 w-4 text-slate-500" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(s.id)}><Trash2 className="h-4 w-4 text-red-400" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
